import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2024 Sequent Tech <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { calcPrecentage, calcTotalApplications, } from "./ElectionEventStats";
import { AuthContext } from "@/providers/AuthContextProvider";
import { IPermissions } from "@/types/keycloak";
import { useTranslation } from "react-i18next";
var useVotersStats = function (props) {
    var eligibleVotersCount = props.eligibleVotersCount, enrolledVotersCount = props.enrolledVotersCount, approvalStats = props.approvalStats, authenticationStats = props.authenticationStats;
    var t = useTranslation().t;
    var authContext = useContext(AuthContext);
    var showTotalEnrolledVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_ENROLLED_OVERSEAS_VOTERS);
    var showAllApproveDisapproveVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_ALL_APPROVE_DISAPPROVE_VOTERS);
    var showManuallyApproveDisapproveVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_MANUALLY_APPROVE_DISAPPROVE_VOTERS);
    var showAutomaticApproveDisapproveVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_AUTOMATIC_APPROVE_DISAPPROVE_VOTERS);
    var showAuthenticatedVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITOR_AUTHENTICATED_VOTERS);
    var votersSection = useMemo(function () {
        var total_auth_errors = 0;
        if (typeof authenticationStats.invalidUsersErrorsCount === "number" &&
            typeof authenticationStats.invalidPasswordErrorsCount === "number") {
            total_auth_errors =
                authenticationStats.invalidUsersErrorsCount +
                    authenticationStats.invalidPasswordErrorsCount;
        }
        return {
            show: showTotalEnrolledVoters ||
                showAllApproveDisapproveVoters ||
                showManuallyApproveDisapproveVoters ||
                showAutomaticApproveDisapproveVoters ||
                showAuthenticatedVoters,
            title: t("monitoringDashboardScreen.voters.title"),
            stats: [
                {
                    show: showTotalEnrolledVoters,
                    title: t("monitoringDashboardScreen.voters.enrolledOverseasVoters"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: enrolledVotersCount,
                            percentage: calcPrecentage(enrolledVotersCount, eligibleVotersCount),
                        },
                    ],
                },
                {
                    show: showAllApproveDisapproveVoters,
                    title: t("monitoringDashboardScreen.voters.approvalStatus"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: approvalStats.approvedCount,
                            percentage: calcPrecentage(approvalStats.approvedCount, calcTotalApplications(approvalStats.approvedCount, approvalStats.disapprovedCount)),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: approvalStats.disapprovedCount,
                            percentage: calcPrecentage(approvalStats.disapprovedCount, calcTotalApplications(approvalStats.approvedCount, approvalStats.disapprovedCount)),
                        },
                    ],
                },
                {
                    show: showManuallyApproveDisapproveVoters,
                    title: t("monitoringDashboardScreen.voters.manuallyApproval"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: approvalStats.manualApprovedCount,
                            percentage: calcPrecentage(approvalStats.manualApprovedCount, calcTotalApplications(approvalStats.manualApprovedCount, approvalStats.manualDisapprovedCount)),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: approvalStats.manualDisapprovedCount,
                            percentage: calcPrecentage(approvalStats.manualDisapprovedCount, calcTotalApplications(approvalStats.manualApprovedCount, approvalStats.manualDisapprovedCount)),
                        },
                    ],
                },
                {
                    show: showAutomaticApproveDisapproveVoters,
                    title: t("monitoringDashboardScreen.voters.automaticallyApproval"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: approvalStats.automatedApprovedCount,
                            percentage: calcPrecentage(approvalStats.automatedApprovedCount, calcTotalApplications(approvalStats.automatedApprovedCount, approvalStats.automatedDisapprovedCount)),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: approvalStats.automatedDisapprovedCount,
                            percentage: calcPrecentage(approvalStats.automatedDisapprovedCount, calcTotalApplications(approvalStats.automatedApprovedCount, approvalStats.automatedDisapprovedCount)),
                        },
                    ],
                },
                {
                    show: showAuthenticatedVoters,
                    title: t("monitoringDashboardScreen.voters.authenticatedVoters"),
                    items: [
                        {
                            icon: _jsx(CheckCircleOutlineIcon, {}),
                            count: authenticationStats.authenticatedCount,
                            percentage: calcPrecentage(authenticationStats.authenticatedCount, enrolledVotersCount),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            count: authenticationStats.notAuthenticatedCount,
                            percentage: calcPrecentage(authenticationStats.notAuthenticatedCount, enrolledVotersCount),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            info: t("monitoringDashboardScreen.voters.invalidUserErrors"),
                            count: authenticationStats.invalidUsersErrorsCount,
                            percentage: calcPrecentage(authenticationStats.invalidUsersErrorsCount, total_auth_errors),
                        },
                        {
                            icon: _jsx(CancelOutlinedIcon, {}),
                            info: t("monitoringDashboardScreen.voters.invalidPasswordErrors"),
                            count: authenticationStats.invalidPasswordErrorsCount,
                            percentage: calcPrecentage(authenticationStats.invalidPasswordErrorsCount, total_auth_errors),
                        },
                    ],
                },
            ],
        };
    }, [
        authenticationStats,
        enrolledVotersCount,
        showAuthenticatedVoters,
        approvalStats,
        eligibleVotersCount,
        showAutomaticApproveDisapproveVoters,
        showManuallyApproveDisapproveVoters,
        showAllApproveDisapproveVoters,
        showTotalEnrolledVoters,
    ]);
    return { votersSection: votersSection };
};
export default useVotersStats;
